import { observer } from 'mobx-react-lite'
import { InfiniteScrollLoaderContainer } from '@/components/InfiniteScrollLoader/InfiniteScrollLoaderContainer'
import { ConfirmSheet } from '@/components/ConfirmSheet'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { EmptyState, EmptyStateDescription } from '@/components/EmptyState'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import {
  JobNotesCard,
  JobNotesCardList,
  JobNotesDeleteConfirmContent,
} from '@/features/jobs/details/components/JobNotesCard'
import { JobNoteItemContainer } from '@/features/jobs/details/components/JobNotesCard/JobNoteItem/JobNoteItemContainer'

export interface JobNotesCardContainerProps {
  vm: JobDetailsViewModel
}

export const JobNotesCardContainer = observer(function JobNotesCardContainer({
  vm,
}: JobNotesCardContainerProps) {
  const notesVm = vm.jobNotesVm

  const renderNotes = () =>
    notesVm.notes.length ? (
      <JobNotesCardList>
        {notesVm.notes.map((note) => (
          <JobNoteItemContainer key={note.id} vm={notesVm} noteVm={note} />
        ))}
        <InfiniteScrollLoaderContainer
          compact
          scrollProvider={notesVm.scroller}
        />
      </JobNotesCardList>
    ) : (
      <EmptyState size="card" padTop>
        <EmptyStateDescription>No notes on this job yet.</EmptyStateDescription>
      </EmptyState>
    )

  return (
    <JobNotesCard
      isHighlighted={notesVm.isHighlighted}
      canWrite={notesVm.canWrite}
      onAddNote={notesVm.addNoteVm.show}
      isMovedNoticeShowing={notesVm.movedNotice.showing}
      onCloseMovedNotice={notesVm.movedNotice.close}
      deleteConfirmSheet={
        <ConfirmSheet
          danger
          yes="Delete note"
          showing={notesVm.deleteNoteDialogVm.modal.showing}
          isPending={notesVm.deleteNoteDialogVm.delete.pending}
          onConfirm={notesVm.deleteNoteDialogVm.delete}
          onDecline={notesVm.deleteNoteDialogVm.modal.close}
        >
          <JobNotesDeleteConfirmContent />
        </ConfirmSheet>
      }
    >
      {notesVm.scroller.fetch.match({
        pending: () =>
          notesVm.isRevalidating ? (
            renderNotes()
          ) : (
            <ContentLoading size="section" />
          ),
        rejected: (err) => (
          <ContentError
            size="section"
            title="Error loading notes."
            error={err}
          />
        ),
        resolved: renderNotes,
      })}
    </JobNotesCard>
  )
})
